import { useAuth } from '@agentnet/auth';
import { createStyles, makeStyles, MenuItem } from '@material-ui/core';
import { enqueueFilesApi, getTeamUsers } from 'api/orderManagement/order-management-api';
import {
  getKeyByValue,
  getMemberNames,
  ProcessingStatus,
  ProcessingStatusColor,
  ReasonCode,
  TeamAssignment,
} from 'features/orderManagement/enum';
import {
  FileEditAssignmentType,
  OrderFileQueueResponseType,
  OrderFilesRequestType,
  UserDetailType,
} from 'features/orderManagement/types';
import useAsync from 'hooks/useAsync';
import { useEffect, useState } from 'react';
import { AgentNetConfirmationDialog } from 'ui-kit/components/modal/ConfirmationDialog';
import { AgentNetDropdownSelector, AgentNetTextInput } from 'ui-kit/inputs';
import { SelectOption } from 'ui-kit/inputs/AgentNetDropdownSelector';
import AutocompleteSearch from 'ui-kit/inputs/AutocompleteSearch/AutocompleteSearch';
import { getFullName } from 'utilities/utilities';
import { getStatusForTeamMove, isTeamSelectable } from './FileEditAssignmentContainer.utils';

interface FileEditAssignmentProps {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  data: FileEditAssignmentType[];
  onSaved?: () => void;
}

function firstOrDefault<T>(data: T[]): T | null {
  return data?.[0] || null;
}

function getFileStatus(data: FileEditAssignmentType[]): ProcessingStatus | null {
  const first = firstOrDefault(data)?.status;
  return first && data?.every((datum) => datum.status === first)
    ? ProcessingStatus[first as keyof typeof ProcessingStatus]
    : null;
}

function getFileReasonCode(data: FileEditAssignmentType[]): ReasonCode | null {
  const first = firstOrDefault(data)?.reasonCode;
  return first && data?.every((datum) => datum.reasonCode === first)
    ? ReasonCode[first as keyof typeof ReasonCode]
    : null;
}

function getFileTeam(data: FileEditAssignmentType[]): TeamAssignment | null {
  const first = firstOrDefault(data)?.assignedToGroup;
  return first && data?.every((datum) => datum.assignedToGroup === first)
    ? TeamAssignment[first as keyof typeof TeamAssignment]
    : null;
}

function getFileAssigneeId(data: FileEditAssignmentType[]): number | null {
  const first = firstOrDefault(data)?.caseFileOwnerId;
  return first && data?.every((datum) => datum.caseFileOwnerId === first) ? first : null;
}

function getFileAssigneeName(data: FileEditAssignmentType[]): string | null {
  const first = firstOrDefault(data)?.fullName;
  return first && data?.every((datum) => datum.fullName === first) ? first : null;
}

function getFileAssigneeUsername(data: FileEditAssignmentType[]): string | null {
  const first = firstOrDefault(data)?.assignedTo;
  return first && data?.every((datum) => datum.assignedTo === first) ? first : null;
}

export const FileEditAssignmentContainer = ({ open, setOpen, data, onSaved }: FileEditAssignmentProps): JSX.Element => {
  const fileStatus = getFileStatus(data);
  const fileReasonCode = getFileReasonCode(data);
  const fileTeam = getFileTeam(data);

  const [status, setStatus] = useState<ProcessingStatus | null>();
  const [reasonCode, setReasonCode] = useState<ReasonCode | null>();
  const [team, setTeam] = useState<TeamAssignment | null>();
  const [assigneeId, setAssigneeId] = useState<number | null>(getFileAssigneeId(data));
  const [assigneeName, setAssigneeName] = useState<string | null>(getFileAssigneeName(data));
  const [assigneeUsername, setAssigneeUsername] = useState<string | null>(getFileAssigneeUsername(data));
  const [teamMembers, setTeamMembers] = useState<UserDetailType[]>([]);
  const [comment, setComment] = useState<string>('');

  // Dirty state tracking - tracks which fields the user has explicitly changed
  const [isTeamDirty, setIsTeamDirty] = useState(false);
  const [isAssigneeDirty, setIsAssigneeDirty] = useState(false);
  const [isStatusDirty, setIsStatusDirty] = useState(false);
  const [isReasonCodeDirty, setIsReasonCodeDirty] = useState(false);

  // Mixed values detection - true when files have different values (distinct from "all null")
  const hasMixedTeam = data.length > 1 && fileTeam === null && data.some((d) => d.assignedToGroup);
  const hasMixedAssignee = data.length > 1 && getFileAssigneeId(data) === null && data.some((d) => d.caseFileOwnerId);
  const hasMixedStatus = data.length > 1 && fileStatus === null && data.some((d) => d.status);
  const hasMixedReasonCode = data.length > 1 && fileReasonCode === null && data.some((d) => d.reasonCode);

  const { getAccessToken } = useAuth();

  const useStyles = makeStyles(() =>
    createStyles({
      routeContainer: {
        width: '100%',
      },
      circle: {
        width: 12,
        height: 12,
        display: 'inline-block',
        marginRight: 4,
        borderRadius: 12,
      },
      separator: {
        paddingBottom: 20,
      },
    }),
  );

  const classes = useStyles();

  const getTeamSelectOptions = (): SelectOption[] => {
    const teamSelectOptions: SelectOption[] = [];

    // Add "-- Mixed Values --" placeholder when files have different teams and user hasn't changed it
    if (hasMixedTeam && !isTeamDirty) {
      teamSelectOptions.push({
        value: '',
        name: '-- Mixed Values --',
        disabled: true,
      });
    }

    const teamAssignments = getMemberNames(TeamAssignment);
    let teamAssignment = teamAssignments.next();
    while (!teamAssignment.done) {
      const teamAssignmentValue = TeamAssignment[teamAssignment.value as keyof typeof TeamAssignment];
      if (isTeamSelectable(fileTeam, teamAssignmentValue)) {
        teamSelectOptions.push({
          value: teamAssignmentValue,
          name: teamAssignment.value,
        });
      }
      teamAssignment = teamAssignments.next();
    }
    return teamSelectOptions;
  };

  // Sentinel value for mixed assignees (AutocompleteSearch ignores empty string values)
  const MIXED_ASSIGNEE_VALUE = '__MIXED__';

  const getAssigneeSelectOptions = (): SelectOption[] => {
    const assigneeSelectOptions: SelectOption[] = [];

    // Add "-- Mixed Values --" placeholder when files have different assignees and user hasn't changed it
    if (hasMixedAssignee && !isAssigneeDirty) {
      assigneeSelectOptions.push({
        value: MIXED_ASSIGNEE_VALUE,
        name: '-- Mixed Values --',
      });
    }

    for (const teamMember of teamMembers) {
      const fullName = getFullName(teamMember);
      assigneeSelectOptions.push({
        value: teamMember.userId,
        name: `${fullName}`,
        tooltip: teamMember.username,
      });
    }
    return assigneeSelectOptions.sort((a, b) => {
      // Keep mixed values option at top
      if (a.value === MIXED_ASSIGNEE_VALUE) return -1;
      if (b.value === MIXED_ASSIGNEE_VALUE) return 1;
      return a.name?.localeCompare(b.name || '') || 0;
    });
  };

  const getReasonCodeSelectOptions = (): SelectOption[] => {
    const reasonCodesSelectOptions: SelectOption[] = [];

    // Add "-- Mixed Values --" placeholder when files have different reason codes and user hasn't changed it
    if (hasMixedReasonCode && !isReasonCodeDirty) {
      reasonCodesSelectOptions.push({
        value: '',
        name: '-- Mixed Values --',
        disabled: true,
      });
    }

    const reasonCodes = Array.from(getMemberNames(ReasonCode)).sort((a, b) => a.localeCompare(b));
    for (const reasonCode of reasonCodes) {
      reasonCodesSelectOptions.push({
        value: ReasonCode[reasonCode as keyof typeof ReasonCode],
        name: reasonCode,
      });
    }
    reasonCodesSelectOptions.push({
      value: null,
      name: '',
    });
    return reasonCodesSelectOptions;
  };

  const getFileStatusMenuItems = (): JSX.Element[] => {
    const fileStatusMenuItems: JSX.Element[] = [];

    // Add "-- Mixed Values --" placeholder when files have different statuses and user hasn't changed it
    if (hasMixedStatus && !isStatusDirty) {
      fileStatusMenuItems.push(
        <MenuItem key="mixed-values" value="" disabled>
          -- Mixed Values --
        </MenuItem>,
      );
    }

    const fileStatuses = Array.from(getMemberNames(ProcessingStatus)).sort((a, b) => a.localeCompare(b));
    for (const fileStatusKey of fileStatuses) {
      // Hide "Closed" status from UI selection (but keep it valid in the system)
      if (fileStatusKey !== 'Closed') {
        const fileStatusValue = ProcessingStatus[fileStatusKey as keyof typeof ProcessingStatus];
        const fileStatusColor = ProcessingStatusColor[fileStatusKey as keyof typeof ProcessingStatusColor];
        const menuItem = (
          <MenuItem key={fileStatusKey} value={fileStatusValue}>
            <span
              className={classes.circle}
              style={{ backgroundColor: '#' + fileStatusColor.toString(16).padStart(6, '0') }}
            ></span>
            {fileStatusKey}
          </MenuItem>
        );
        fileStatusMenuItems.push(menuItem);
      }
    }
    return fileStatusMenuItems;
  };

  const getStatus = () => status || fileStatus;
  const getTeam = () => team || fileTeam;
  const getReasonCode = () => (reasonCode === undefined ? fileReasonCode : reasonCode);
  const getAssigneeId = () => assigneeId;
  const getAssigneeName = () => assigneeName;
  const getAssigneeUsername = () => assigneeUsername;

  const teamChanged = getTeam() !== fileTeam;
  const selectedStatus = teamChanged ? getStatusForTeamMove(fileTeam, getTeam()) : getStatus();

  const enqueueFiles = async (): Promise<OrderFileQueueResponseType> => {
    const token = await getAccessToken();

    // Helper to generate a grouping key for files based on original values of non-dirty fields
    const getGroupKey = (file: FileEditAssignmentType): string => {
      const parts: string[] = [String(file.orderId ?? 0)];

      // Include original team in key if team is not dirty
      if (!isTeamDirty) {
        const originalTeam = file.assignedToGroup
          ? TeamAssignment[file.assignedToGroup as keyof typeof TeamAssignment]
          : 0;
        parts.push(`t:${originalTeam ?? 0}`);
      }

      // Include original assignee in key if assignee is not dirty
      if (!isAssigneeDirty) {
        parts.push(`a:${file.caseFileOwnerId ?? 0}`);
      }

      // Include original status in key if status is not dirty
      if (!isStatusDirty) {
        const originalStatus = file.status ? ProcessingStatus[file.status as keyof typeof ProcessingStatus] : 0;
        parts.push(`s:${originalStatus ?? 0}`);
      }

      // Include original reason code in key if reason code is not dirty
      if (!isReasonCodeDirty) {
        const originalReasonCode = file.reasonCode ? ReasonCode[file.reasonCode as keyof typeof ReasonCode] : 0;
        parts.push(`r:${originalReasonCode ?? 0}`);
      }

      return parts.join('|');
    };

    // Group files by orderId and original values for non-dirty fields
    const fileGroups = data.reduce((acc, file) => {
      const key = getGroupKey(file);
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(file);
      return acc;
    }, {} as Record<string, FileEditAssignmentType[]>);

    // Make API calls for each group
    const apiCalls = Object.values(fileGroups).map((files) => {
      const firstFile = files[0];
      const orderId = firstFile.orderId ?? 0;

      // For dirty fields, use the new value; for non-dirty fields, use original from first file in group
      const originalTeam = firstFile.assignedToGroup
        ? TeamAssignment[firstFile.assignedToGroup as keyof typeof TeamAssignment]
        : null;
      const originalAssigneeId = firstFile.caseFileOwnerId ?? null;
      const originalStatus = firstFile.status ? ProcessingStatus[firstFile.status as keyof typeof ProcessingStatus] : 0;
      const originalReasonCode = firstFile.reasonCode ? ReasonCode[firstFile.reasonCode as keyof typeof ReasonCode] : 0;

      const payload: OrderFilesRequestType = {
        orderId: orderId,
        fileIds: files.map((file) => file.fileId),
        fileStatusId: isStatusDirty ? selectedStatus ?? 0 : originalStatus ?? 0,
        reasonCodeId: isReasonCodeDirty ? getReasonCode() ?? 0 : originalReasonCode ?? 0,
        fscRoleId: isTeamDirty ? getTeam() : originalTeam,
        userId: isAssigneeDirty ? getAssigneeId() : originalAssigneeId,
        comment: comment,
      };
      return enqueueFilesApi(orderId, payload, token);
    });

    // Execute all API calls in parallel and return the first response (they should all have same structure)
    const responses = await Promise.all(apiCalls);
    return responses[0];
  };

  const getTeamMembers = async (): Promise<Array<UserDetailType>> => {
    const fscRoleId = getTeam();
    if (fscRoleId) {
      const token = await getAccessToken();
      const teamMembers = await getTeamUsers(Number(fscRoleId), token);
      return teamMembers;
    }
    return [];
  };

  const {
    execute: executeEnqueueFiles,
    status: enqueueFilesStatus,
    errors: enqueueFilesErrors,
  } = useAsync<OrderFileQueueResponseType>(enqueueFiles, false);

  const {
    execute: executeGetTeamMembers,
    status: getTeamMembersStatus,
    value: getTeamMembersValue,
  } = useAsync<Array<UserDetailType>>(getTeamMembers, false);

  const onConfirm = () => {
    executeEnqueueFiles();
  };

  const onDismissAction = () => {
    // Reset to undefined (not null) so getters fall back to file values on next open
    setStatus(undefined);
    setTeam(undefined);
    setAssigneeId(null);
    setAssigneeName(null);
    setAssigneeUsername(null);
    setReasonCode(undefined);
    // Reset dirty flags
    setIsTeamDirty(false);
    setIsAssigneeDirty(false);
    setIsStatusDirty(false);
    setIsReasonCodeDirty(false);
    setOpen(false);
  };

  // Sync state with data prop when it changes (e.g., new rows selected)
  useEffect(() => {
    // Reset field states to undefined so getters fall back to file values
    setStatus(undefined);
    setTeam(undefined);
    setReasonCode(undefined);
    setAssigneeId(getFileAssigneeId(data));
    setAssigneeName(getFileAssigneeName(data));
    setAssigneeUsername(getFileAssigneeUsername(data));
    // Reset dirty flags when data changes
    setIsTeamDirty(false);
    setIsAssigneeDirty(false);
    setIsStatusDirty(false);
    setIsReasonCodeDirty(false);
    // Clear team members so they get refetched for the new data
    setTeamMembers([]);
  }, [data]);

  // Fetch team members when team changes or when data changes (new files selected)
  useEffect(() => {
    const currentTeam = getTeam();
    if (currentTeam) {
      executeGetTeamMembers();
    }
  }, [team, fileTeam, data]);

  useEffect(() => {
    if (enqueueFilesStatus === 'success') {
      if (data) {
        for (const datum of data) {
          // Only update fields that were explicitly changed (dirty)
          if (isTeamDirty) {
            datum.assignedToGroup = getKeyByValue(TeamAssignment, getTeam()) ?? '';
          }
          if (isAssigneeDirty) {
            datum.caseFileOwnerId = getAssigneeId() ?? 0;
            datum.assignedTo = getAssigneeUsername() ?? '';
            datum.fullName = getAssigneeName() ?? '';
          }
          if (isStatusDirty) {
            datum.status = getKeyByValue(ProcessingStatus, selectedStatus) ?? '';
            datum.fileStatus = getKeyByValue(ProcessingStatus, selectedStatus) ?? '';
            datum.fileStatusId = selectedStatus ?? 0;
          }
          if (isReasonCodeDirty) {
            datum.reasonCode = getKeyByValue(ReasonCode, getReasonCode()) ?? '';
          }
        }
      }
      if (onSaved) {
        onSaved();
      }
      onDismissAction();
    } else if (enqueueFilesStatus === 'error') {
      console.error(enqueueFilesErrors);
    }
  }, [enqueueFilesStatus, onSaved]);

  useEffect(() => {
    if (getTeamMembersStatus === 'success' && getTeamMembersValue) {
      setTeamMembers(getTeamMembersValue);
    }
  }, [getTeamMembersStatus]);

  return (
    <AgentNetConfirmationDialog
      open={open}
      dialogTitle="Modify Assignment"
      dialogTextHTML={
        <>
          <div className={classes.separator}>
            <AgentNetDropdownSelector
              label="Team"
              name="Team"
              value={getTeam() || ''}
              options={getTeamSelectOptions()}
              menuOption={(value: any) => {
                setTeam(value);
                setIsTeamDirty(true);
                // When team changes, clear assignee and mark as dirty
                if (value !== fileTeam) {
                  setAssigneeId(null);
                  setAssigneeName(null);
                  setAssigneeUsername(null);
                  setIsAssigneeDirty(true);
                  // Status will be defaulted for this team move (via teamChanged logic), mark it dirty
                  setIsStatusDirty(true);
                } else {
                  // Team reverted to original, just clear the assignee
                  setAssigneeId(null);
                  setAssigneeName(null);
                  setAssigneeUsername(null);
                }
              }}
            />
          </div>
          <div className={classes.separator}>
            <AutocompleteSearch
              label="Assignee"
              name="assignee"
              required={true}
              data-testid={'Edit-Assignment-Assignee'}
              hideSearchIcon
              disableFilterOptions={true}
              options={getAssigneeSelectOptions()}
              noMatchesLabel=""
              value={hasMixedAssignee && !isAssigneeDirty ? MIXED_ASSIGNEE_VALUE : getAssigneeId() || ''}
              disabled={hasMixedTeam && !isTeamDirty}
              helperText={hasMixedTeam && !isTeamDirty ? 'Select a team to enable assignee selection' : undefined}
              onChange={(event) => {
                if (typeof event === 'number') {
                  const user = teamMembers.find((member) => member.userId === event);
                  const name = user ? getFullName(user) : '';
                  setAssigneeId(event);
                  setAssigneeName(name);
                  setAssigneeUsername(user?.username ?? '');
                  setIsAssigneeDirty(true);
                }
              }}
            />
          </div>
          <div className={classes.separator}>
            <AgentNetDropdownSelector
              label="Status"
              name="Status"
              value={selectedStatus || ''}
              required
              disabled={teamChanged}
              customMenuItems={getFileStatusMenuItems()}
              menuOption={(value: any) => {
                setStatus(value);
                setIsStatusDirty(true);
              }}
            />
          </div>
          <div className={classes.separator}>
            <AgentNetDropdownSelector
              label="Reason Code"
              name="Reason Code"
              value={getReasonCode() || ''}
              options={getReasonCodeSelectOptions()}
              menuOption={(value: any) => {
                setReasonCode(value);
                setIsReasonCodeDirty(true);
              }}
            />
          </div>
          <div className={classes.separator}>
            <AgentNetTextInput
              label="Comment"
              name="Comment"
              variant="outlined"
              fullWidth
              multiline
              rows="2"
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        </>
      }
      dialogBtnContent="Save"
      secondaryActionBtnContent="Cancel"
      onDismissAction={onDismissAction}
      onConfirm={onConfirm}
      disabled={enqueueFilesStatus === 'pending'}
      disableBackDropClick
    />
  );
};
